<template>
  <!-- Modal y no un panel al pie.
       Como panel, la entrega aparecia al final de la pagina: desde el listado
       el operador apretaba «Comprobante» sobre una fila de arriba y no pasaba
       nada visible hasta que scrolleaba. Lo reporto el operador el 2026-09-07.
       Sobre la fila, la accion y su resultado quedan en el mismo lugar. -->
  <teleport to="body">
  <div v-if="abierto">
    <div class="modal fade show" style="display: block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content vx-panel" data-test="panel-comprobante">
    <div class="vx-panel-cabecera">
      <h2 class="vx-panel-titulo">
        <template v-if="notaCreditoId">Nota de crédito</template>
        <template v-else>Comprobante del cobro #{{ cobroId }}</template>
      </h2>
      <button class="vx-btn vx-btn-sm" data-test="cerrar-comprobante" @click="cerrar">
        Cerrar
      </button>
    </div>

    <div class="p-3" style="max-height: calc(100vh - 180px); overflow-y: auto">
      <div v-if="cargando" class="vx-vacio">
        <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
        <span class="vx-vacio-texto">Buscando el comprobante…</span>
      </div>

      <!--
        Hasta DOS documentos (SPEC_NOTAS_DE_CREDITO RN-149): si el cobro se
        anuló con una nota de crédito —o, en el recibo, con su constancia de
        anulación—, ese documento va ARRIBA, porque es el que el pagador tiene
        que recibir ahora; y debajo el documento anulado, marcado como tal.
        Cada uno con sus acciones: una sola forma de entregar, para los dos
        (RN-141).
      -->
      <template v-else-if="documentos.length">
        <section v-for="doc in documentos" :key="doc.prefijo"
                 class="vx-entrega mb-3" :data-test="`bloque-${doc.prefijo}`">
          <template v-if="doc.disponible">
            <div class="vx-aviso" :class="{ 'vx-aviso-anulado': doc.anulado }">
              <div class="vx-aviso-titulo">
                <i class="bi" :class="doc.anulado ? 'bi-x-octagon' : 'bi-patch-check'"></i>
                <span :data-test="`${doc.prefijo}-titulo`">
                  {{ tituloDe(doc) }}
                  <template v-if="doc.numero">N.º {{ doc.numero }}</template>
                </span>
                <span v-if="doc.anulado" class="vx-estado vx-estado-anulado ms-2"
                      :data-test="`${doc.prefijo}-anulado`">Anulado</span>
              </div>
              <!-- El aviso es el MISMO texto que ve el pagador en el enlace: lo
                   arma el backend con una sola función (RY-13). -->
              <div v-if="doc.anulacion?.aviso" class="mt-1" :data-test="`${doc.prefijo}-aviso-anulacion`">
                {{ doc.anulacion.aviso }}
              </div>
              <div v-if="doc.documento_acreditado" class="vx-ayuda mt-1">
                Anula {{ doc.tipo === 'RECIBO_ANULACION' ? 'el recibo N.º' : 'la factura' }}
                {{ doc.documento_acreditado }}.
              </div>
              <div class="vx-ayuda mt-1">
                El enlace es público: el pagador lo abre sin usuario ni contraseña.
              </div>
            </div>

            <div class="d-flex flex-wrap gap-2 mt-3">
              <template v-if="doc.pdf_url">
                <button class="vx-btn vx-btn-sm" :data-test="`${doc.prefijo}-pdf`"
                        @click="verPdf = verPdf === doc.prefijo ? null : doc.prefijo">
                  <i class="bi bi-file-earmark-pdf"></i>
                  {{ verPdf === doc.prefijo ? 'Ocultar PDF' : 'Ver PDF' }}
                </button>
                <a class="vx-btn vx-btn-sm" :href="doc.pdf_url" target="_blank" rel="noopener"
                   :data-test="`${doc.prefijo}-pdf-pestana`">
                  <i class="bi bi-box-arrow-up-right"></i> Abrir en otra pestaña
                </a>
              </template>
              <a v-if="doc.xml_url" class="vx-btn vx-btn-sm" :href="doc.xml_url" target="_blank" rel="noopener"
                 :data-test="`${doc.prefijo}-xml`">
                <i class="bi bi-filetype-xml"></i> Descargar XML
              </a>
              <button class="vx-btn vx-btn-sm" :data-test="`${doc.prefijo}-copiar`" @click="copiar(doc)">
                <i class="bi bi-link-45deg"></i>
                {{ copiado === doc.prefijo ? 'Enlace copiado' : 'Copiar enlace' }}
              </button>
              <button class="vx-btn vx-btn-sm" :data-test="`${doc.prefijo}-whatsapp`" @click="docWhatsapp = doc">
                <i class="bi bi-whatsapp"></i> Enviar por WhatsApp
              </button>
              <!-- El ticket termico. Va acá y no sólo en la pantalla del cobro
                   porque el pagador pide el comprobante DESPUÉS, y ahí el operador
                   entra por el listado. -->
              <button v-if="doc.movimiento_id" class="vx-btn vx-btn-sm"
                      :data-test="`${doc.prefijo}-imprimir`" :disabled="imprimiendo === doc.prefijo"
                      @click="imprimirTicket(doc)">
                <span v-if="imprimiendo === doc.prefijo" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="bi bi-printer"></i>
                {{ imprimiendo === doc.prefijo ? 'Imprimiendo…' : 'Imprimir ticket' }}
              </button>
            </div>

            <!-- Mirarlo antes de entregarlo, sin salir de la pantalla ni depender
                 de que el navegador deje abrir una pestaña nueva. -->
            <div v-if="verPdf === doc.prefijo" class="mt-3" :data-test="`${doc.prefijo}-visor`">
              <iframe :src="doc.pdf_url" title="Comprobante en PDF"
                      style="width: 100%; height: 55vh; border: 1px solid var(--vx-borde); border-radius: 6px"></iframe>
            </div>

            <!-- La impresora es una comodidad, no la entrega: el enlace ya existe.
                 Por eso el fallo se cuenta con la salida al lado (H-AD). -->
            <div v-if="errorImpresion && errorImpresion.prefijo === doc.prefijo" class="vx-error mt-3"
                 :data-test="`${doc.prefijo}-error-impresion`">
              {{ errorImpresion.texto }}
              <div class="vx-ayuda mt-1">
                Mandale el enlace por WhatsApp: está acá abajo.
              </div>
            </div>

            <!--
              El enlace a la vista, no sólo en el portapapeles: si copiar falla
              —permisos del navegador, contexto no seguro— el operador todavía
              puede seleccionarlo a mano.
            -->
            <div class="vx-ayuda mt-2 vx-num" style="word-break: break-all"
                 :data-test="`${doc.prefijo}-url`">{{ doc.url }}</div>

            <div v-if="!doc.url_propia" class="vx-nota mt-3" :data-test="`${doc.prefijo}-sin-marca`">
              <i class="bi bi-info-circle"></i>
              <span>
                El enlace apunta directo a facturación electrónica porque esta
                instancia todavía no tiene configurada su dirección pública de
                comprobantes. Funciona igual, pero sin la marca del comercio.
              </span>
            </div>
          </template>

          <div v-else class="vx-vacio" :data-test="`${doc.prefijo}-no-disponible`">
            <i class="bi bi-exclamation-circle" style="font-size: 22px; color: var(--vx-texto-4)"></i>
            <span class="vx-vacio-titulo">No hay {{ tituloDe(doc).toLowerCase() }} para entregar</span>
            <span v-if="doc.anulacion?.aviso" class="vx-vacio-texto" :data-test="`${doc.prefijo}-aviso-anulacion`">
              {{ doc.anulacion.aviso }}
            </span>
            <span class="vx-vacio-texto">{{ doc.motivo }}</span>
          </div>
        </section>
      </template>

      <div v-else class="vx-vacio" data-test="comprobante-no-disponible">
        <i class="bi bi-exclamation-circle" style="font-size: 22px; color: var(--vx-texto-4)"></i>
        <span class="vx-vacio-titulo">No hay comprobante para entregar</span>
        <span class="vx-vacio-texto">{{ error }}</span>
      </div>
    </div>

        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" @click="cerrar"></div>

    <EnviarSeguimientoModal
      :open="Boolean(docWhatsapp)"
      :tracking-url="docWhatsapp?.url || ''"
      :tipo-documento="docWhatsapp?.tipo || 'FACTURA'"
      :mensaje="mensajeWhatsapp"
      :telefono-inicial="docWhatsapp?.contacto?.telefono || telefono"
      @cerrado="docWhatsapp = null"
    />
  </div>
  </teleport>
</template>

<script setup>
// Entrega del comprobante — E10 (CR-E10-006) · RN-58, RN-59, RN-60 ·
// SPEC_NOTAS_DE_CREDITO RN-141, RN-145, RN-149, RN-151, RN-153.
//
// Vive en un componente y no dentro de las vistas porque lo necesitan TRES
// pantallas: la del cobro recién hecho, el listado de cobros y el de notas de
// crédito. Y el que importa es el del listado — es adonde vuelve el operador
// cuando el pagador le pide el comprobante otra vez (H-AB).
//
// No arma la URL ni el aviso: los pide. Quién puede entregar qué, por qué
// enlace, y qué se le dice al pagador de un documento anulado, lo decide el
// backend — que es el único que sabe si esta instancia tiene dirección pública.

import { computed, ref, watch } from 'vue'
import EnviarSeguimientoModal from '@/components/EnviarSeguimientoModal.vue'
import { obtenerComprobante } from '@/services/cobranzaService'
import { obtenerComprobanteNotaCredito } from '@/services/notasCreditoService'
import { mensajeDeEntrega } from '@/utils/mensajeDeEntrega.mjs'
import cashboxLocalPrintService from '@/services/cashboxLocalPrintService'
import localPrintSettingsService from '@/services/localPrintSettingsService'
import { useNotificationStore } from '@/stores/notifications'

/* global defineProps, defineEmits */
const props = defineProps({
  abierto: { type: Boolean, default: false },
  // Una de las dos: el comprobante de un cobro (con su documento anulado y el
  // de anulación, si los hay), o una nota de crédito por su id.
  cobroId: { type: [Number, String], default: null },
  notaCreditoId: { type: [Number, String], default: null },
  telefono: { type: String, default: '' }
})
const emit = defineEmits(['cerrado'])

const notificaciones = useNotificationStore()

const respuesta = ref(null)
const cargando = ref(false)
const error = ref('')
const copiado = ref(null)
const docWhatsapp = ref(null)
const verPdf = ref(null)
const imprimiendo = ref(null)
const errorImpresion = ref(null)

const TITULOS = {
  FACTURA: 'Factura electrónica',
  RECIBO: 'Recibo de dinero',
  NOTA_CREDITO: 'Nota de crédito electrónica',
  RECIBO_ANULACION: 'Constancia de anulación'
}
const tituloDe = (doc) => TITULOS[doc?.tipo] || 'Comprobante'

// El documento de anulación arriba, el anulado debajo (RN-149). El prefijo es
// también el de los `data-test`: el principal conserva `comprobante-*`, que es
// lo que los E2E de E10 ya miran.
const documentos = computed(() => {
  const r = respuesta.value
  if (!r) return []
  if (props.notaCreditoId) return [{ ...r, prefijo: 'comprobante' }]
  const lista = []
  if (r.documento_anulacion) lista.push({ ...r.documento_anulacion, prefijo: 'anulacion' })
  lista.push({ ...r, prefijo: 'comprobante' })
  return lista
})

const mensajeWhatsapp = computed(() =>
  (docWhatsapp.value ? mensajeDeEntrega(docWhatsapp.value, docWhatsapp.value.url) : ''))

watch(() => [props.abierto, props.cobroId, props.notaCreditoId], async ([abierto, cobroId, ncId]) => {
  if (!abierto || (!cobroId && !ncId)) return
  cargando.value = true
  error.value = ''
  copiado.value = null
  verPdf.value = null
  errorImpresion.value = null
  docWhatsapp.value = null
  try {
    respuesta.value = ncId
      ? await obtenerComprobanteNotaCredito(ncId)
      : await obtenerComprobante(cobroId)
  } catch (err) {
    respuesta.value = null
    error.value = err?.userMessage || 'No se pudo leer el comprobante.'
  } finally {
    cargando.value = false
  }
}, { immediate: true })

async function copiar (doc) {
  try {
    await navigator.clipboard.writeText(doc?.url || '')
    copiado.value = doc.prefijo
    setTimeout(() => { copiado.value = null }, 2500)
  } catch (_) {
    // El enlace se muestra igual debajo: copiar es la comodidad, verlo es la
    // garantía.
    copiado.value = null
  }
}

// El agente de impresion corre en la PC del operador (`pos-printer`, puerto
// 9977) y la llamada nace en el navegador, no en el backend — regla de
// integracion con agentes locales de `AGENTS.md`.
async function imprimirTicket (doc) {
  const movimientoId = doc?.movimiento_id
  if (!movimientoId || imprimiendo.value) return

  imprimiendo.value = doc.prefijo
  errorImpresion.value = null
  try {
    const settings = localPrintSettingsService.getSettings()
    if (!settings.enabled) {
      errorImpresion.value = {
        prefijo: doc.prefijo,
        texto: 'La impresión local está desactivada. Activala en '
          + 'Mantenimiento › Operativo para imprimir desde esta pantalla.'
      }
      return
    }
    await cashboxLocalPrintService.printMovementReceipt({ movementId: movimientoId, settings })
    notificaciones.notify({ type: 'success', message: 'Ticket enviado a la impresora.' })
  } catch (err) {
    errorImpresion.value = {
      prefijo: doc.prefijo,
      texto: err?.userMessage || err?.message || 'La impresora térmica no está disponible.'
    }
  } finally {
    imprimiendo.value = null
  }
}

function cerrar () {
  emit('cerrado')
}
</script>

<style scoped>
.vx-aviso-anulado { border-color: #e8b4ad; background: #fdf3f1; }
.vx-estado-anulado { background: #a5281a; color: #fff; }
.vx-entrega + .vx-entrega { border-top: 1px solid var(--vx-borde); padding-top: 16px; }
</style>
